import React from 'react';
import { Train } from '../types';
import { Card, CardContent, Divider, Typography, Box, Chip, Accordion, AccordionSummary, AccordionDetails, Tooltip, Alert, useTheme, useMediaQuery } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Grid';
import TrainIcon from '@mui/icons-material/Train';
import ArrowRightAlt from '@mui/icons-material/ArrowRightAlt';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpIcon from '@mui/icons-material/Help';
import { format, parseISO, differenceInMinutes } from 'date-fns';

import rttLogo from '../assets/rtt_logo_white.svg';
import darwinLogo from '../assets/darwin_logo.svg';
import { getStationCrs } from '../services/storage';

export const formatDiscrepancyForMobile = (discrepancy: string): string => {
  return discrepancy
    .replace(/\s*\(RTT showed on time\)/gi, '')
    .replace(/\s*\(missing from RTT\)/gi, '')
    .trim();
};

// Helper function to format time strings for display
const formatTime = (timeString: string | null | undefined): string => {
    if (!timeString) return '';
    try {
	return format(parseISO(timeString), 'HH:mm');
    } catch (error) {
	console.error('Invalid time string:', timeString, error);
	return '';
    }
};



const calculateDuration = (startTime?: string | null, endTime?: string | null): number | null => {
  if (!startTime || !endTime) return null;
  try {
    const start = parseISO(startTime);
    const end = parseISO(endTime);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    return differenceInMinutes(end, start);
  } catch {
    return null;
  }
};

interface TrainCardProps {
    train: Train;
    destination: string;
    showOriginCrs?: boolean;
}

const TrainCard: React.FC<TrainCardProps> = ({ train, destination, showOriginCrs = false }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDepartureDelayed = Boolean(train.expected_departure && train.scheduled_departure && train.expected_departure !== train.scheduled_departure);
    const isArrivalDelayed = Boolean(train.expected_arrival && train.scheduled_arrival && train.expected_arrival !== train.scheduled_arrival);
    const isCancelled = !!train.cancellation_reason;

    const scheduledDuration = calculateDuration(train.scheduled_departure, train.scheduled_arrival);
    const expectedDuration = calculateDuration(train.expected_departure, train.expected_arrival);

    const renderTimeWithDelay = (scheduledTime?: string | null, actualTime?: string | null) => {
	const formattedScheduled = formatTime(scheduledTime);
	const formattedActual = formatTime(actualTime);
	if (formattedActual && formattedScheduled && actualTime && scheduledTime) {
	    try {
		const actualDt = parseISO(actualTime);
		const schedDt = parseISO(scheduledTime);
		if (actualDt > schedDt && formattedActual !== formattedScheduled) {
		    return (
			<>
			    <Typography component="span" sx={{ ml: 0.5, textDecoration: 'line-through', fontSize: 'inherit' }}>
				{formattedScheduled}
			    </Typography>
			    <Typography component="span" color="error" sx={{ ml: 0.5, fontSize: 'inherit' }}>
				{formattedActual}
			    </Typography>
			</>
		    );
		}
	    } catch {
		// fallback
	    }
	}
	return formattedScheduled || formattedActual || '';
    };

  const renderDarwinDetails = () => {
    if (!train.darwin_info) return null;

    const formatDarwinDeparture = () => {
      if (!train.darwin_info) return '';
      const sched = formatTime(train.darwin_info.scheduled_departure);
      const exp = formatTime(train.darwin_info.expected_departure);
      const status = train.darwin_info.status;

      if (status && /^\d{2}:\d{2}$/.test(status)) {
        return sched && sched !== status ? `${status} (scheduled ${sched})` : status;
      }
      if (exp && sched && exp !== sched) {
        return `${exp} (scheduled ${sched})`;
      }
      if (status && sched && status.toLowerCase() !== 'on time') {
        return `${status} (scheduled ${sched})`;
      }
      if (status && sched) {
        return `${sched} (${status})`;
      }
      return status || sched || '';
    };

    return (
      <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8125rem' }}>
            Darwin (National Rail)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {train.darwin_info.discrepancy && (
              <Chip
                label={train.darwin_info.discrepancy}
                size="small"
                color={train.cancellation_reason ? 'error' : 'warning'}
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
            {train.darwin_info.darwin_url && (
              <a
                href={train.darwin_info.darwin_url}
                target="_blank"
                rel="noopener noreferrer"
                title="View on Darwin Browser"
                style={{
                  backgroundColor: '#0b3d66',
                  padding: '4px 6px',
                  borderRadius: '4px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                }}
              >
                <img src={darwinLogo} alt="View on Darwin Browser" style={{ height: '10px', width: '10px' }} />
              </a>
            )}
          </Box>
        </Box>
        <Box sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'divider', mt: 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
            <strong>Departure ({train.from_station}):</strong> {formatDarwinDeparture()}
            {train.darwin_info.platform ? ` • Platform ${train.darwin_info.platform}` : ''}
            {train.darwin_info.length ? ` • ${train.darwin_info.length} coaches` : ''}
          </Typography>
          {train.darwin_info.scheduled_arrival && (
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
              <strong>Destination ({train.user_destination_station}):</strong> Scheduled {formatTime(train.darwin_info.scheduled_arrival)}
              {train.darwin_info.expected_arrival && train.darwin_info.expected_arrival !== train.darwin_info.scheduled_arrival ? ` (expected ${formatTime(train.darwin_info.expected_arrival)})` : ''}
              {train.darwin_info.destination_platform ? ` • Plat ${train.darwin_info.destination_platform}` : ''}
            </Typography>
          )}
          {train.arrival_source === 'darwin' && (
            <Typography variant="body2" color="info.main" sx={{ fontSize: '0.75rem' }}>
              <strong>Arrival fallback:</strong> Destination arrival time supplied by Darwin
            </Typography>
          )}
          {train.destination_platform_source === 'darwin' && (
            <Typography variant="body2" color="info.main" sx={{ fontSize: '0.75rem' }}>
              <strong>Platform fallback:</strong> Destination platform supplied by Darwin
            </Typography>
          )}
          {train.darwin_info.delay_reason && (
            <Typography variant="body2" color="warning.main" sx={{ fontSize: '0.75rem' }}>
              <strong>Delay reason:</strong> {train.darwin_info.delay_reason}
            </Typography>
          )}
          {train.darwin_info.cancel_reason && (
            <Typography variant="body2" color="error.main" sx={{ fontSize: '0.75rem' }}>
              <strong>Cancellation reason:</strong> {train.darwin_info.cancel_reason}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const renderRttDetails = (isUnannouncedDelay: boolean = false) => {
    return (
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8125rem' }}>
            Realtime Trains ({train.service_uid})
          </Typography>
          <a
            href={train.rtt_link}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              backgroundColor: '#1b7285',
              padding: '2px 4px',
              borderRadius: '3px',
              display: 'inline-flex',
              alignItems: 'center',
              textDecoration: 'none',
            }}
          >
            <img src={rttLogo} alt="View on Realtime Trains" style={{ height: '9px' }} />
          </a>
        </Box>
        {train.rtt_error && (
          <Alert severity="warning" sx={{ my: 0.5, py: 0, px: 1, fontSize: '0.75rem' }}>
            <strong>RTT lookup error:</strong> {train.rtt_error}
          </Alert>
        )}
        <Box sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'divider', mt: 0.5 }}>
          {train.origin_station_info && (
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
              <strong>Origin:</strong> {train.origin_station_info.name} at {formatTime(train.origin_station_info.scheduled_departure)}
            </Typography>
          )}
          {train.last_departed_station && (!train.associated_services || train.associated_services.length === 0) && (
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
              <strong>Last Departed:</strong> {train.last_departed_station.name} at {formatTime(train.last_departed_station.actual_departure)} (scheduled {formatTime(train.last_departed_station.scheduled_departure)})
            </Typography>
          )}
          {train.last_passed_location && (!train.associated_services || train.associated_services.length === 0) && (
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
              <strong>Last Passed:</strong> {train.last_passed_location.name} at {formatTime(train.last_passed_location.actual_pass)}{train.last_passed_location.scheduled_pass ? ` (scheduled ${formatTime(train.last_passed_location.scheduled_pass)})` : ''}
            </Typography>
          )}
          {train.rtt_info?.scheduled_arrival && (
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
              <strong>Destination ({train.user_destination_station}):</strong> Scheduled {formatTime(train.rtt_info.scheduled_arrival)}
              {train.rtt_info.expected_arrival && train.rtt_info.expected_arrival !== train.rtt_info.scheduled_arrival ? ` (expected ${formatTime(train.rtt_info.expected_arrival)})` : ''}
              {train.rtt_info.destination_platform && train.rtt_info.destination_platform !== 'TBC' ? ` • Plat ${train.rtt_info.destination_platform}` : ''}
            </Typography>
          )}
          {isUnannouncedDelay && (
            <Typography variant="body2" color="error" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
              <strong>Delay:</strong> Unannounced delay (departure time passed without report)
            </Typography>
          )}
          {train.rtt_info?.delay_reason && (
            <Typography variant="body2" color="warning.main" sx={{ fontSize: '0.75rem' }}>
              <strong>Delay reason:</strong> {train.rtt_info.delay_reason}
            </Typography>
          )}
          {train.rtt_info?.cancellation_reason && (
            <Typography variant="body2" color="error.main" sx={{ fontSize: '0.75rem' }}>
              <strong>Cancellation reason:</strong> {train.rtt_info.cancellation_reason}
            </Typography>
          )}
        </Box>
        {train.associated_services && train.associated_services.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {train.associated_services.map((service, index) => {
              const match = train.rtt_link.match(/\d{4}-\d{2}-\d{2}/);
              const dateStr = match ? match[0] : '';
              const assocRttLink = `https://www.realtimetrains.co.uk/service/gb-nr:${service.service_uid}/${dateStr}/detailed`;

              let typeText = '';
              switch (service.type) {
                case 'next': typeText = 'Inbound service'; break;
                case 'previous': typeText = 'Formed from service'; break;
                case 'join': typeText = 'Joins with service'; break;
                case 'split': typeText = 'Splits from service'; break;
                default: typeText = 'Associated service';
              }

              const isAssocCancelled = !!service.cancellation_reason;

              return (
                <Box key={service.service_uid} sx={{ mb: index < train.associated_services.length - 1 ? 1 : 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                      {typeText} ({service.service_uid})
                    </Typography>
                    <a
                      href={assocRttLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        backgroundColor: '#1b7285',
                        padding: '2px 4px',
                        borderRadius: '3px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        textDecoration: 'none',
                      }}
                    >
                      <img src={rttLogo} alt="View on Realtime Trains" style={{ height: '9px' }} />
                    </a>
                  </Box>
                  <Box sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'divider', mt: 0.5 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      <strong>Origin:</strong> {service.origin_station_info?.name || 'Unknown'} at {formatTime(service.origin_station_info?.scheduled_departure)}
                    </Typography>
                    {service.last_departed_station && (
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        <strong>Last Departed:</strong> {service.last_departed_station.name} at {formatTime(service.last_departed_station.actual_departure)} (scheduled {formatTime(service.last_departed_station.scheduled_departure)})
                      </Typography>
                    )}
                    {service.last_passed_location && (
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        <strong>Last Passed:</strong> {service.last_passed_location.name} at {formatTime(service.last_passed_location.actual_pass)}{service.last_passed_location.scheduled_pass ? ` (scheduled ${formatTime(service.last_passed_location.scheduled_pass)})` : ''}
                      </Typography>
                    )}
                    {service.destination_station_info && (
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        <strong>Arrival:</strong> {service.destination_station_info.name} at {formatTime(service.destination_station_info.actual_arrival)} (scheduled {formatTime(service.destination_station_info.scheduled_arrival)})
                      </Typography>
                    )}
                    {isAssocCancelled && (
                      <Typography variant="body2" color="error" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                        <strong>Cancelled:</strong> {service.cancellation_reason}
                      </Typography>
                    )}
                    {service.delay_reason && (
                      <Typography variant="body2" color="warning.main" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                        <strong>Delayed:</strong> {service.delay_reason}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  const renderStatusChip = () => {
    if (train.associated_services && train.associated_services.length > 0) {
      const anyCancelled = train.associated_services.some(s => !!s.cancellation_reason);
      const anyDelayed = train.associated_services.some(s => 
          (!!s.destination_station_info && !!s.destination_station_info.actual_arrival && (
            (!!s.destination_station_info.scheduled_arrival && parseISO(s.destination_station_info.actual_arrival) > parseISO(s.destination_station_info.scheduled_arrival)) ||
            (!!train.scheduled_departure && parseISO(s.destination_station_info.actual_arrival) > parseISO(train.scheduled_departure))
          )) ||
          (!!s.last_departed_station && !!s.last_departed_station.scheduled_departure && parseISO(s.last_departed_station.actual_departure) > parseISO(s.last_departed_station.scheduled_departure))
      );

      const summaryParts: React.ReactNode[] = train.associated_services.map((service, index) => {
        const isAssocCancelled = !!service.cancellation_reason;
        const isDelayed = (
          (!!service.destination_station_info && !!service.destination_station_info.actual_arrival && (
            (!!service.destination_station_info.scheduled_arrival && parseISO(service.destination_station_info.actual_arrival) > parseISO(service.destination_station_info.scheduled_arrival)) ||
            (!!train.scheduled_departure && parseISO(service.destination_station_info.actual_arrival) > parseISO(train.scheduled_departure))
          )) ||
          (!!service.last_departed_station && !!service.last_departed_station.scheduled_departure && parseISO(service.last_departed_station.actual_departure) > parseISO(service.last_departed_station.scheduled_departure))
        );

        const originName = service.origin_station_info?.name || 'Unknown';
        const originLabel = isMobile && service.origin_station_info?.name
          ? getStationCrs(service.origin_station_info.name, train)
          : originName;

        if (service.type === 'previous') {
          const destInfo = service.destination_station_info;
          const verb = destInfo?.arrived ? 'arrived' : 'arriving';
          const timeNode = destInfo && (destInfo.actual_arrival || destInfo.scheduled_arrival)
            ? renderTimeWithDelay(destInfo.scheduled_arrival, destInfo.actual_arrival)
            : null;

          if (isAssocCancelled) {
            return `Incoming service from ${originLabel} (Cancelled)`;
          }

          if (timeNode) {
            return (
              <span key={service.service_uid || index}>
                Incoming service from {originLabel} {verb} {timeNode}
              </span>
            );
          }

          return `Incoming service from ${originLabel}${isDelayed ? ' (Delayed)' : ''}`;
        }

        let title = '';
        switch (service.type) {
          case 'next': title = 'Inbound'; break;
          case 'join': title = 'Joins'; break;
          case 'split': title = 'Splits'; break;
          default: title = 'Assoc';
        }
        let suffix = '';
        if (isAssocCancelled) {
          suffix = ' (Cancelled)';
        } else if (isDelayed) {
          suffix = ' (Delayed)';
        }
        return `${title} from ${originLabel}${suffix}`;
      });

      const summaryContent = summaryParts.map((part, index) => (
        <React.Fragment key={index}>
          {index > 0 && ' & '}
          {part}
        </React.Fragment>
      ));

      return (
        <Accordion
          elevation={0}
          disableGutters
          sx={{
            border: '1px solid',
            borderColor: anyCancelled ? 'error.main' : anyDelayed ? 'warning.main' : 'success.main',
            borderRadius: '16px !important',
            bgcolor: 'background.paper',
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 0 },
            minHeight: 0,
            display: 'inline-block',
            width: '100%',
            maxWidth: '600px',
            mt: 0.5,
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.8125rem', color: anyCancelled ? 'error.main' : anyDelayed ? 'warning.main' : 'success.main' }} />}
            sx={{
              minHeight: '24px !important',
              height: 'auto',
              py: 0.25,
              px: 1.5,
              m: 0,
              '& .MuiAccordionSummary-content': {
                m: '0 !important',
                display: 'flex',
                alignItems: 'center',
              },
              color: anyCancelled ? 'error.main' : anyDelayed ? 'warning.main' : 'success.main',
              fontSize: '0.8125rem',
              fontWeight: 500,
            }}
          >
            {summaryContent}
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            {renderRttDetails(false)}
            {renderDarwinDetails()}
          </AccordionDetails>
        </Accordion>
      );
    }

    let summaryText: React.ReactNode = '';
    let color = 'default.main';
    let isUnannounced = false;
    let isDelayed = false;

    if (!train.last_departed_station && train.origin_station_info?.scheduled_departure && train.origin_station_info?.name) {
      const now = new Date();
      try {
        const originDepartureTime = parseISO(train.origin_station_info.scheduled_departure);
        if (!isNaN(originDepartureTime.getTime()) && now > originDepartureTime) {
          const originLabel = isMobile
            ? getStationCrs(train.origin_station_info.name, train)
            : train.origin_station_info.name;
          summaryText = `Unannounced delay at ${originLabel}`;
          color = 'error.main';
          isUnannounced = true;
        }
      } catch {
        // Ignore date parsing failure
      }
    }

    if (!summaryText && train.last_departed_station) {
      isDelayed = Boolean(
        train.last_departed_station.scheduled_departure &&
        train.last_departed_station.actual_departure &&
        parseISO(train.last_departed_station.actual_departure) > parseISO(train.last_departed_station.scheduled_departure)
      );
      const departedLabel = isMobile
        ? getStationCrs(train.last_departed_station.name, train)
        : train.last_departed_station.name;
      summaryText = (
        <>
          Departed {departedLabel} at{' '}
          {renderTimeWithDelay(train.last_departed_station.scheduled_departure, train.last_departed_station.actual_departure)}
        </>
      );
      color = isDelayed ? 'warning.main' : 'success.main';
    }

    if (!summaryText && train.origin_station_info?.name) {
      const originLabel = isMobile
        ? getStationCrs(train.origin_station_info.name, train)
        : train.origin_station_info.name;
      summaryText = `Expected departure from ${originLabel} at ${formatTime(train.origin_station_info.scheduled_departure)}`;
      color = 'success.main';
    }

    if (train.darwin_info?.discrepancy && !isUnannounced && !train.last_departed_station) {
      summaryText = isMobile
        ? formatDiscrepancyForMobile(train.darwin_info.discrepancy)
        : train.darwin_info.discrepancy;
      color = train.cancellation_reason ? 'error.main' : 'warning.main';
    }

    if (!summaryText && train.darwin_info) {
      summaryText = `Darwin: ${train.darwin_info.status}`;
      color = train.cancellation_reason ? 'error.main' : (isDepartureDelayed ? 'warning.main' : 'success.main');
    }

    if (!summaryText) {
      return <Chip icon={<HelpIcon />} label="Status not available" size="small" variant="outlined" />;
    }

    return (
      <Accordion
        elevation={0}
        disableGutters
        sx={{
          border: '1px solid',
          borderColor: color,
          borderRadius: '16px !important',
          bgcolor: 'background.paper',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
          minHeight: 0,
          display: 'inline-block',
          width: '100%',
          maxWidth: '600px',
          mt: 0.5,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.8125rem', color: color }} />}
          sx={{
            minHeight: '24px !important',
            height: 'auto',
            py: 0.25,
            px: 1.5,
            m: 0,
            '& .MuiAccordionSummary-content': {
              m: '0 !important',
              display: 'flex',
              alignItems: 'center',
            },
            color: color,
            fontSize: '0.8125rem',
            fontWeight: 500,
          }}
        >
          {summaryText}
        </AccordionSummary>
        <AccordionDetails sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          {renderRttDetails(isUnannounced)}
          {renderDarwinDetails()}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
              {isCancelled ? <CancelIcon sx={{ mr: 1 }} color="error" /> : <TrainIcon sx={{ mr: 1 }} color={train.first_arrival ? 'success' : 'inherit'}
/>}

              <Typography
                variant="h5"
                sx={{ textDecoration: isDepartureDelayed ? 'line-through' : 'none' }}
              >
                {formatTime(train.scheduled_departure)}
              </Typography>
              {isDepartureDelayed && (
                <Typography variant="h5" color="error" sx={{ ml: 1 }}>
                  {formatTime(train.expected_departure)}
                </Typography>
              )}
              {showOriginCrs && Boolean(train.from_crs) && (
                <Typography variant="h5" color="text.secondary">
                  &nbsp;({train.from_crs})
                </Typography>
              )}
              <ArrowRightAlt sx={{ mx: 1 }} />
              <Box sx={{ display: 'inline-flex', alignItems: 'baseline' }}>
                <Typography 
                  variant="h5"
                  sx={{ textDecoration: isArrivalDelayed ? 'line-through' : 'none' }}
                >
                  {formatTime(train.scheduled_arrival)}
                </Typography>
                {isArrivalDelayed && (
                  <Typography variant="h5" color="error" sx={{ ml: 1 }}>
                    {formatTime(train.expected_arrival)}
                  </Typography>
                )}
                {train.arrival_source === 'darwin' && (
                  <Tooltip title="Arrival time provided by Darwin fallback" arrow>
                    <Typography
                      component="sup"
                      aria-label="Arrival time provided by Darwin fallback"
                      sx={{
                        fontSize: '0.65em',
                        verticalAlign: 'super',
                        ml: 0.25,
                        fontWeight: 'bold',
                        cursor: 'help',
                        color: 'text.secondary',
                      }}
                    >
                      ᴰ
                    </Typography>
                  </Tooltip>
                )}
              </Box>
              {train.user_destination_station !== destination && (
                <>
                  <Typography variant="h5" color="text.secondary">
                      &nbsp;({train.to_crs})
                  </Typography>
                </>
              )}
              {train.user_destination_station !== train.end_of_line_station && (
                <>
                  <ArrowRightAlt sx={{ mx: 1 }} color="disabled" />
                  <Typography variant="h5" component="div" color="text.secondary">
                    {train.end_of_line_station}
                  </Typography>
                </>
              )}
              
              <Chip label={train.operator} size="small" sx={{ ml: 'auto' }} />
            </Box>
          <Divider aria-hidden="true" />

          </Grid>

          { /* Sub-grid for indented detail content */ }
          <Grid container sx={{ ml:4 }} width="100%" spacing={0.5}>
              
          {/* Display Cancellation or Delay Reason */}
          {(train.cancellation_reason || train.delay_reason) && (
            <Grid size={{ xs: 12 }} sx={{ mb: 1 }}>
              <Chip
                label={train.cancellation_reason || train.delay_reason}
                size="small"
                variant="outlined"
                color={train.cancellation_reason ? 'error' : 'warning'}
                icon={train.cancellation_reason ? <CancelIcon /> : <AccessTimeIcon />}
              />
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6, md: 4 }} >
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
              <Typography color="text.secondary">Platform</Typography>
              &nbsp;
              <Chip
                label={train.departure_platform}
                color={train.departure_platform_confirmed ? 'success' : 'default'}
                size="small"
                variant="outlined"
              />
              <ArrowRightAlt sx={{ mx: 1 }}  />
              <Chip
                label={
                  train.destination_platform_source === 'darwin' ? (
                    <Tooltip title="Platform provided by Darwin fallback" arrow>
                      <span>{train.destination_platform}ᴰ</span>
                    </Tooltip>
                  ) : (
                    train.destination_platform
                  )
                }
                color={train.destination_platform_confirmed ? 'success' : 'default'}
                size="small"
                variant="outlined"
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }} >
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography color="text.secondary">Duration:&nbsp;</Typography>
              {scheduledDuration !== null ? (
                <>
                  <span style={{ textDecoration: (isDepartureDelayed || isArrivalDelayed) && expectedDuration !== null && expectedDuration !== scheduledDuration ? 'line-through' : 'none' }}>
                    {scheduledDuration}m
                  </span>
                  {(isDepartureDelayed || isArrivalDelayed) && expectedDuration !== null && expectedDuration !== scheduledDuration && (
                    <span style={{ color: 'red', marginLeft: '4px' }}>
                      {expectedDuration}m
                    </span>
                  )}
                </>
              ) : (
                <Typography component="span" color="text.secondary">N/A</Typography>
              )}
            </Box>
          </Grid>
          <Grid size={{ xs: 12 }}>
            {renderStatusChip()}
          </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default TrainCard;
